import { memo, useMemo } from "react";
import _ from "lodash";

import { EMPTY_LIST } from "utils/constants";

import Box from "components/Box";
import SimpleTable from "components/SimpleTable";
import Stack from "components/Stack";

import {
  formatHeaders,
  formatRows,
} from "entities/Memo/components/Sections/ExcelTable/utils";
import SectionContent from "entities/Memo/components/Sections/SectionContent";
import SectionHeader from "entities/Memo/components/Sections/SectionHeader";
import {
  IMemoSection,
  IMemoSectionExcelTableFieldValue,
} from "entities/Memo/sdk";

interface IPrintableSectionExcelTable {
  section: IMemoSection;
}

const PrintableSectionExcelTable = ({
  section,
}: IPrintableSectionExcelTable) => {
  const field = useMemo(() => _.first(section.fields), [section]);
  const fieldValue = useMemo(
    () => _.get(field, "values.[0].column_value", EMPTY_LIST),
    [field]
  ) as Array<IMemoSectionExcelTableFieldValue>;

  /**
   * The field values is in the format Array<{order: number, columns: string[]}>
   * The element with order 0 is the header row, and the rest of the elements are the data rows.
   */
  const defaultHeaders = useMemo(() => {
    if (_.isUndefined(fieldValue)) {
      return EMPTY_LIST;
    }

    const headerRow = _.find(fieldValue, { order: 0 });

    if (_.isUndefined(headerRow)) {
      return EMPTY_LIST;
    }

    return formatHeaders({ headers: headerRow.columns });
  }, [fieldValue]);

  /**
   * The default value is an array of objects, where each object represents a row in the table.
   * The keys of the object are the headers of the table, that is, the first element of fieldValue and parsed in headers.
   */
  const defaultValue = useMemo(() => {
    if (_.isUndefined(fieldValue)) {
      return EMPTY_LIST;
    }

    const rows = fieldValue
      .filter((row) => row?.order !== 0)
      .map((row) => row?.columns);

    const formattedRows = formatRows({
      rows,
      headers: defaultHeaders,
    });

    return formattedRows;
  }, [fieldValue, defaultHeaders]);

  const showInputToPasteData = useMemo(
    () => _.isEmpty(defaultValue),
    [defaultValue]
  );

  return (
    <Box>
      <SectionHeader label={section.title} alignActionButton="right" />
      <SectionContent>
        <Box
          sx={{
            marginTop: "8px",
            "& .MuiIconButton-root": { visibility: "hidden" },
            "&:hover .MuiIconButton-root": {
              visibility: "visible",
            },
          }}
        >
          {!showInputToPasteData && (
            <Stack
              spacing={2}
              sx={{
                ...(section.table_section_compact_view
                  ? {
                      "tr, td, th, table, tbody": {
                        padding: 0.5,
                        borderTop: 0,
                        borderLeft: 0,
                        borderRight: 0,
                      },
                    }
                  : {}),
              }}
            >
              <SimpleTable
                headers={defaultHeaders}
                rows={defaultValue}
                size="small"
              />
            </Stack>
          )}
        </Box>
      </SectionContent>
    </Box>
  );
};

export default memo(PrintableSectionExcelTable, (prevProps, nextProps) =>
  _.isEqual(prevProps, nextProps)
);
